import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const ImageUploader = ({ images = [], onChange, max = 6 }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const reorder = (from, to) => {
    if (from == null || from === to) return;
    const next = [...images];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };

  const handleFiles = async (files) => {
    const list = [];
    for (const f of Array.from(files).slice(0, max - images.length)) {
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} ist zu groß (max. 8 MB)`); continue; }
      list.push(f);
    }
    if (list.length === 0) return;
    setUploading(true);
    const uploaded = [];
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Upload fehlgeschlagen");
      }
    }
    if (uploaded.length) onChange([...images, ...uploaded]);
    setUploading(false);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {images.map((url, i) => (
          <div key={i} draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => { reorder(dragIndex, i); setDragIndex(null); }}
            className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200 group cursor-move" data-testid={`uploaded-img-${i}`}>
            <img src={url} alt="" className="w-full h-full object-cover pointer-events-none" />
            {i === 0 && <span className="absolute bottom-0 inset-x-0 bg-emerald-600 text-white text-[10px] text-center font-semibold py-0.5">Titel</span>}
            <button type="button" onClick={() => onChange(images.filter((_, x) => x !== i))}
              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`remove-img-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {images.length < max && (
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-emerald-500 hover:text-emerald-600 transition-colors"
            data-testid="upload-trigger">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple hidden data-testid="file-input"
        onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
      <p className="text-xs text-slate-400">JPG, PNG, WEBP oder GIF · max. {max} Bilder · bis 8 MB · ziehen zum Sortieren, erstes Bild = Titelbild</p>
    </div>
  );
};
